/* Hover melody.
 *
 * One shared tune for the whole site, advanced by exactly one note per hover.
 * Which project you hover, in what order, at what speed — none of it matters.
 * Hover project 7, then 2, then 19 and you still hear note 1, note 2, note 3.
 * The song is a queue, not a mapping.
 *
 * The tune is drawn at random once per page load, so every refresh is a
 * different track. Pool is at the bottom.
 */

const MELODIES = {
  /* --- originals ------------------------------------------------------- */

  /* Not a tune so much as a rising ostinato: a six-note shape cycled through
     Am - F - C - G, moved onto each new root. No rhythm to lose, no phrase to
     interrupt, and it climbs, so the longer someone browses the bigger it
     gets. Hover fast and the long tails stack into a chord. */
  ascent: {
    voice: 'organ',
    notes: [
      'A3','E4','A4','C5','B4','E4',
      'F3','C4','F4','A4','G4','C4',
      'C4','G4','C5','E5','D5','G4',
      'G3','D4','G4','B4','A4','D4',
    ],
  },

  /* Same idea, colder and wider. */
  drift: {
    voice: 'organ',
    notes: [
      'D3','A3','D4','F4','E4','A3',
      'Bb2','F3','Bb3','D4','C4','F3',
      'G3','D4','G4','Bb4','A4','D4',
      'A2','E3','A3','C4','B3','E4',
    ],
  },

  /* --- public domain --------------------------------------------------- */

  /* Grieg. Escalates the longer someone browses. */
  mountainKing: {
    voice: 'pluck',
    notes: [
      'B3','C#4','D4','E4','F#4','D4','F#4',
      'F4','C#4','F4','E4','C4','E4',
      'B3','C#4','D4','E4','F#4','D4','F#4',
      'A4','G4','F#4','E4','D4','B3',
    ],
  },

  /* Korobeiniki. Russian trad, and the reason everyone under fifty knows it. */
  korobeiniki: {
    voice: 'pluck',
    notes: [
      'E5','B4','C5','D5','C5','B4','A4','A4','C5','E5',
      'D5','C5','B4','C5','D5','E5','C5','A4','A4',
      'D5','F5','A5','G5','F5','E5','C5','E5','D5','C5','B4','B4','C5','D5','E5','C5','A4','A4',
    ],
  },

  /* Bach, BWV 565. Dropped an octave so the organ stack doesn't scream. */
  toccata: {
    voice: 'organ',
    notes: [
      'A4','G4','A4',
      'G4','F4','E4','D4','C#4','D4',
      'A3','G3','A3',
      'E3','F3','D3','C#3','D3',
    ],
  },

  /* Beethoven, Moonlight, first movement. Already an arpeggio ostinato, so it
     survives any hover rhythm you throw at it. */
  moonlight: {
    voice: 'organ',
    notes: [
      'G#3','C#4','E4','G#3','C#4','E4',
      'G#3','C#4','E4','G#3','C#4','E4',
      'A3','C#4','E4','A3','C#4','E4',
      'A3','D4','F#4','A3','D4','F#4',
    ],
  },

  /* Pachelbel. The progression everything else is built on. */
  canon: {
    voice: 'bell',
    notes: [
      'F#5','E5','D5','C#5','B4','A4','B4','C#5',
      'D5','C#5','B4','A4','G4','F#4','G4','E4',
      'D5','F#5','A5','G5','F#5','D5','F#5','E5',
    ],
  },

  /* Bizet, Habanera. A chromatic slide down, so it slinks at any speed. */
  habanera: {
    voice: 'pluck',
    notes: [
      'D5','C#5','C5','B4','Bb4','A4','A4','A4',
      'Bb4','B4','C5','C#5','D5','E5','F5',
      'E5','D5','C#5','C5','B4','Bb4','A4',
    ],
  },

  /* Wagner. Kept low and rumbling. */
  valkyries: {
    voice: 'organ',
    notes: [
      'B2','F#3','B3','D4','B3','D4',
      'F#4','D4','F#4','B4','F#4','D4',
      'B3','D4','F#4','B3','F#3','B2',
    ],
  },

  /* Trad. English. */
  greensleeves: {
    voice: 'bell',
    notes: [
      'A4','C5','D5','E5','F5','E5','D5','B4',
      'G4','A4','B4','C5','A4','A4','G#4','A4','B4','G#4','E4',
      'D5','D5','C5','A4','A4','G4','A4','B4','C5','A4','A4','G#4','A4','B4','G#4','E4',
    ],
  },

  /* --- defined but out of rotation; swap into POOL if you want them ----- */
  odeToJoy: {
    voice: 'bell',
    notes: [
      'E4','E4','F4','G4','G4','F4','E4','D4','C4','C4','D4','E4','E4','D4','D4',
      'E4','E4','F4','G4','G4','F4','E4','D4','C4','C4','D4','E4','D4','C4','C4',
    ],
  },
  furElise: {
    voice: 'bell',
    notes: [
      'E5','D#5','E5','D#5','E5','B4','D5','C5','A4',
      'C4','E4','A4','B4','E4','G#4','B4','C5',
      'E4','E5','D#5','E5','D#5','E5','B4','D5','C5','A4',
    ],
  },
  auldLangSyne: {
    voice: 'bell',
    notes: [
      'C4','F4','F4','F4','A4','G4','F4','G4','A4','G4','F4','F4','A4','C5','D5',
      'D5','C5','A4','A4','F4','G4','F4','G4','A4','G4','F4','D4','D4','C4','F4',
    ],
  },

};

/* The ten in rotation. One is drawn at random per page load. */
const POOL = [
  'ascent', 'drift', 'mountainKing', 'korobeiniki', 'toccata',
  'moonlight', 'canon', 'habanera', 'valkyries', 'greensleeves',
];

const CURRENT = POOL[Math.floor(Math.random() * POOL.length)];

const SEMITONES = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

function freq(note) {
  const m = /^([A-G])(#|b)?(\d)$/.exec(note);
  if (!m) return 440;
  let n = SEMITONES[m[1]];
  if (m[2] === '#') n += 1;
  if (m[2] === 'b') n -= 1;
  const midi = n + (parseInt(m[3], 10) + 1) * 12;
  return 440 * Math.pow(2, (midi - 69) / 12);
}

const TUNE = MELODIES[CURRENT] || MELODIES.ascent;
const SONG = TUNE.notes.map(freq);
const VOICE = TUNE.voice || 'organ';

// so you can see what you got without digging: __tune in the console
if (typeof window !== 'undefined') window.__tune = CURRENT;
export const currentMelody = CURRENT;

/* The keyboard can be switched off from the header. Remembered across pages
   the same way chaos mode is. */
let muted = (() => {
  try { return localStorage.getItem('mode-sound') === '0'; } catch (e) { return false; }
})();

export const isMelodyMuted = () => muted;

export function setMelodyMuted(on) {
  muted = !!on;
  try { localStorage.setItem('mode-sound', muted ? '0' : '1'); } catch (e) {}
}

let ctx = null;
let master = null;
let step = 0;
let lastAt = 0;

// Browsers only allow audio after a real interaction (a click or a keypress —
// hovering doesn't count). Arm it on the first one, then stay armed.
function arm() {
  if (ctx) { if (ctx.state === 'suspended') ctx.resume(); return; }
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  ctx = new AC();

  // notes overlap and stack, so keep a limiter on the end of the chain
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -18;
  comp.ratio.value = 12;
  comp.attack.value = 0.004;
  comp.release.value = 0.25;

  master = ctx.createGain();
  master.gain.value = VOICE === 'organ' ? 0.1 : VOICE === 'worm' ? 0.075 : 0.13;
  master.connect(comp);
  comp.connect(ctx.destination);
}

if (typeof window !== 'undefined') {
  const once = () => arm();
  window.addEventListener('pointerdown', once, { passive: true });
  window.addEventListener('keydown', once, { passive: true });
  window.addEventListener('touchstart', once, { passive: true });
}

/* Sine partials, slow swell, long tail. Hover slowly and each note blooms and
   fades alone; hover fast and they pile into a chord. */
function organNote(f, now) {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(1, now + 0.09);
  g.gain.exponentialRampToValueAtTime(0.35, now + 0.9);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 2.8);
  g.connect(master);

  // drawbar-ish: fundamental, octave, twelfth, two octaves
  const partials = [[1, 1], [2, 0.5], [3, 0.18], [4, 0.12]];
  const stop = now + 2.9;

  partials.forEach(([mult, level], i) => {
    if (f * mult > 6000) return;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = f * mult;
    o.detune.value = i % 2 ? 4 : -4;
    const pg = ctx.createGain();
    pg.gain.value = level;
    o.connect(pg);
    pg.connect(g);
    o.start(now);
    o.stop(stop);
  });
}

/* Bright and short. */
function bellNote(f, now) {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(1, now + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.9);
  g.connect(master);

  const o = ctx.createOscillator();
  o.type = 'triangle';
  o.frequency.value = f;
  o.connect(g);

  const o2 = ctx.createOscillator();
  o2.type = 'sine';
  o2.frequency.value = f * 2;
  const g2 = ctx.createGain();
  g2.gain.value = 0.25;
  o2.connect(g2);
  g2.connect(g);

  o.start(now); o2.start(now);
  o.stop(now + 0.95); o2.stop(now + 0.95);
}

/* Saw through a filter that snaps shut. For the ones that want attack. */
function pluckNote(f, now) {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(1, now + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
  g.connect(master);

  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.Q.value = 6;
  lp.frequency.setValueAtTime(Math.min(f * 9, 9000), now);
  lp.frequency.exponentialRampToValueAtTime(Math.max(f * 1.5, 200), now + 0.35);
  lp.connect(g);

  const o = ctx.createOscillator();
  o.type = 'sawtooth';
  o.frequency.value = f;
  o.connect(lp);

  // a second saw a few cents off, for width
  const o2 = ctx.createOscillator();
  o2.type = 'sawtooth';
  o2.frequency.value = f;
  o2.detune.value = 9;
  const g2 = ctx.createGain();
  g2.gain.value = 0.6;
  o2.connect(g2);
  g2.connect(lp);

  o.start(now); o2.start(now);
  o.stop(now + 0.6); o2.stop(now + 0.6);
}

/* The worm. A near-sine tone up in the top octaves, a heavy glide into every
   note, and a vibrato wide and fast enough to be the point rather than a
   garnish. First pass at this was a square through a resonant filter, which
   read as chiptune; the real character is closer to a whistle that never
   quite settles.

   The glide is what makes it: hover two projects a fifth apart and you hear
   the bend between them, not two separate notes. */
let lastFreq = 0;

function wormNote(f, now) {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(1, now + 0.04);
  g.gain.exponentialRampToValueAtTime(0.6, now + 0.45);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 1.3);
  g.connect(master);

  // just enough of a lid to take the edge off the top notes
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.Q.value = 1.2;
  lp.frequency.value = Math.min(f * 4, 7000);
  lp.connect(g);

  // sine fundamental with a little triangle on top: not pure, not buzzy
  const o = ctx.createOscillator();
  o.type = 'sine';
  const o2 = ctx.createOscillator();
  o2.type = 'triangle';
  const g2 = ctx.createGain();
  g2.gain.value = 0.22;
  o2.connect(g2);
  g2.connect(lp);
  o.connect(lp);

  // portamento: slide in from wherever the last note left off
  [o, o2].forEach((osc) => {
    osc.frequency.setValueAtTime(lastFreq || f, now);
    osc.frequency.exponentialRampToValueAtTime(f, now + 0.15);
  });

  // vibrato: a full semitone, quick, arriving almost with the note
  const lfo = ctx.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.value = 6.5;
  const depth = ctx.createGain();
  depth.gain.setValueAtTime(f * 0.015, now);
  depth.gain.linearRampToValueAtTime(f * 0.06, now + 0.2);
  lfo.connect(depth);
  depth.connect(o.frequency);
  depth.connect(o2.frequency);

  o.start(now); o2.start(now); lfo.start(now);
  o.stop(now + 1.4); o2.stop(now + 1.4); lfo.stop(now + 1.4);

  lastFreq = f;
}

/* Play the next note of the song. Safe to call as fast as a cursor can move —
   anything under 80ms apart is dropped so a sweep across the grid reads as a
   run of notes rather than a smear. */
export function playNextNote() {
  if (muted) return;
  arm();
  if (!ctx || ctx.state !== 'running') return;

  const now = ctx.currentTime;
  if (now - lastAt < 0.08) return;
  lastAt = now;

  const f = SONG[step % SONG.length];
  step += 1;

  if (VOICE === 'bell') bellNote(f, now);
  else if (VOICE === 'pluck') pluckNote(f, now);
  else if (VOICE === 'worm') wormNote(f, now);
  else organNote(f, now);
}

/* Back to the top of the song. Called when the gallery mounts. The track
   itself only changes on a page load. */
export function resetMelody() {
  step = 0;
}

/* Hover colours, lifted from the reward ladder in Art of Running. That ladder
   is 63 colours ordered as a gradient, so taking them in sequence would put
   near-identical neighbours next to each other. These are 27 picked by
   farthest-point in Lab space, the same distinctness rule your distinctPalette
   uses, so no two tiles ever read as the same colour. One per project. */
/* Hover colours, lifted from the reward ladder in Art of Running.
   Two constraints, not one: they have to be far apart from each other so no
   two tiles read alike, and each has to carry the white tile title. Eleven of
   the previous set failed that second test — white type on #ffedca or #c5cdd4
   was barely there — so the pool is now only the ladder colours that hit 3:1
   against white, and the farthest-point pick runs inside that. */
export const HOVER_COLORS = [
  '#da6e2c', '#1b65cf', '#6f9592', '#a13e68',
  '#8d56ab', '#b52524', '#5b8cd1', '#186070',
  '#8067d8', '#ae2d3a', '#a83651', '#c84a28',
  '#9a467e', '#4e828f', '#944e94', '#3072d0',
  '#875fc2', '#467fd0', '#6e96a2', '#d15c2a',
  '#4e66d4', '#35727b', '#be3726', '#6766d6',
  '#528386', '#608c8c', '#266976',
];
